/**
 * Application-level fallback for React Router route errors.
 *
 * Attached as `errorElement` to every top-level route so rendering, loader
 * and action failures show a VxAnnotate page instead of React Router's
 * default error screen.
 */

import { Button } from "@frontend/components/ui/button"
import { isRouteErrorResponse, Link, useRouteError } from "react-router"

function describeError(error: unknown): { title: string; detail: string | null } {
  if (isRouteErrorResponse(error)) {
    return {
      title: `${error.status} ${error.statusText}`.trim(),
      detail: typeof error.data === "string" ? error.data : null
    }
  }
  if (error instanceof Error) {
    return { title: "Something went wrong", detail: error.message }
  }
  return { title: "Something went wrong", detail: null }
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  const { title, detail } = describeError(error)

  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <div className="text-center space-y-4 max-w-md">
        <h1 className="text-2xl font-bold">{title}</h1>
        {detail ? <p className="text-sm text-muted-foreground break-words">{detail}</p> : null}
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button asChild>
            <Link to="/">Back to projects</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
